// AttachmentViewer.js
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const AttachmentViewer = ({ url, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fileType, setFileType] = useState('');
  
  useEffect(() => {
    // Determine file type from URL extension
    const extension = url.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension)) {
      setFileType('image');
    } else if (['pdf'].includes(extension)) {
      setFileType('pdf');
    } else if (['txt', 'log', 'md'].includes(extension)) {
      setFileType('text');
    } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv'].includes(extension)) {
      setFileType('document');
    } else {
      setFileType('unknown');
    }
    setLoading(false);
  }, [url]);

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-full w-full md:w-4/5 lg:w-3/5 max-h-full flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium">Attachment Preview</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 flex-1 overflow-auto flex justify-center items-center">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center p-4">
              <p className="text-red-500">Failed to load attachment</p>
            </div>
          ) : (
            <>
              {fileType === 'image' && (
                <img 
                  src={url} 
                  alt="Attachment" 
                  className="max-w-full max-h-80 md:max-h-96 object-contain"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              )}
              {fileType === 'pdf' && (
                <object
                  data={url}
                  type="application/pdf"
                  className="w-full h-96"
                >
                  <div className="flex flex-col items-center justify-center p-4 bg-gray-100 rounded">
                    <p className="mb-4">Unable to display PDF directly.</p>
                    <a 
                      href={url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Open PDF
                    </a>
                  </div>
                </object>
              )}
              {(fileType === 'text' || fileType === 'document' || fileType === 'unknown') && (
                <div className="flex flex-col items-center justify-center p-4 bg-gray-100 rounded w-full h-64">
                  <p className="mb-2">This file type cannot be previewed directly.</p>
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Download File
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentViewer;